package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhCountItem
import br.com.vvrefeicoes.portal.domain.model.RhDismissed
import br.com.vvrefeicoes.portal.domain.model.RhNetPayrollStatus
import br.com.vvrefeicoes.portal.domain.model.RhOvertime
import br.com.vvrefeicoes.portal.domain.model.RhOvertimeGroup
import br.com.vvrefeicoes.portal.domain.model.RhOvertimeMonth
import br.com.vvrefeicoes.portal.domain.model.RhRanking
import br.com.vvrefeicoes.portal.domain.model.RhSummary
import br.com.vvrefeicoes.portal.domain.model.RhSummaryMonth
import br.com.vvrefeicoes.portal.domain.model.RhSummaryUnit
import br.com.vvrefeicoes.portal.domain.model.RhVacancies
import br.com.vvrefeicoes.portal.domain.model.RhVacancy
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.RhDashboardRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteRhDashboardRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : RhDashboardRepository {
    override suspend fun loadSummary(
        period: MonthPeriod,
        director: String?,
    ): RhSummary = request("$baseUrl/rh/summary?${query(period, director)}").body<RhSummaryDto>().toDomain()

    override suspend fun loadDismissed(
        period: MonthPeriod,
        director: String?,
    ): RhDismissed = request("$baseUrl/rh/dismissed?${query(period, director)}").body<RhDismissedDto>().toDomain()

    override suspend fun loadOvertime(
        period: MonthPeriod,
        director: String?,
    ): RhOvertime = request("$baseUrl/rh/overtime?${query(period, director)}").body<RhOvertimeDto>().toDomain()

    override suspend fun loadVacancies(): RhVacancies = request("$baseUrl/rmp/vacancies").body<RhVacanciesDto>().toDomain()

    private fun query(
        period: MonthPeriod,
        director: String?,
    ): String =
        buildList {
            add("year=${period.year}")
            add("month=${period.month}")
            director?.takeIf { it.isNotBlank() }?.let { add("diretoria=${it.encodeURLParameter()}") }
        }.joinToString("&")

    private suspend fun request(url: String): HttpResponse {
        val response = httpClient.get(url) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<RhDashboardErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class RhSummaryDto(
    val diretorias: List<String> = emptyList(),
    val monthly: List<RhSummaryMonthDto> = emptyList(),
    val current: RhSummaryMonthDto? = null,
    val activeVariation: Double? = null,
    val units: List<RhSummaryUnitDto> = emptyList(),
    val netPayrollByManager: List<RhRankingDto> = emptyList(),
    val netPayrollByDirector: List<RhRankingDto> = emptyList(),
    val netPayrollByGestora: List<RhRankingDto> = emptyList(),
) {
    fun toDomain(): RhSummary =
        RhSummary(
            directors = diretorias,
            monthly = monthly.map(RhSummaryMonthDto::toDomain),
            current = current?.toDomain(),
            activeVariationPercent = activeVariation,
            units = units.map(RhSummaryUnitDto::toDomain),
            netPayrollByManager = netPayrollByManager.map(RhRankingDto::toDomain),
            netPayrollByDirector = netPayrollByDirector.map(RhRankingDto::toDomain),
            netPayrollByGestora = netPayrollByGestora.map(RhRankingDto::toDomain),
        )
}

@Serializable
private data class RhSummaryMonthDto(
    val ano: Int,
    val mes: Int,
    val active: Int = 0,
    val admitted: Int = 0,
    val dismissed: Int = 0,
    val turnover: Double = 0.0,
    val absenteeism: Double? = null,
    val overtime: Double = 0.0,
    val bonus: Double = 0.0,
    val productivity: Double = 0.0,
    val totalCost: Double = 0.0,
    val netPayroll: Double? = null,
    val netPayrollStatus: String? = null,
    val costsImported: Boolean = true,
) {
    fun toDomain(): RhSummaryMonth =
        RhSummaryMonth(
            period = MonthPeriod(year = ano, month = mes),
            active = active,
            admitted = admitted,
            dismissed = dismissed,
            turnoverPercent = turnover,
            absenteeismPercent = absenteeism,
            overtime = overtime,
            bonus = bonus,
            productivity = productivity,
            totalCost = totalCost,
            netPayroll = netPayroll,
            netPayrollStatus =
                RhNetPayrollStatus.entries.firstOrNull { it.name == netPayrollStatus } ?: RhNetPayrollStatus.MISSING,
            costsImported = costsImported,
        )
}

@Serializable
private data class RhSummaryUnitDto(
    val name: String = "",
    val employees: Int = 0,
    val admitted: Int = 0,
    val dismissed: Int = 0,
    val turnover: Double = 0.0,
    val absenteeism: Double? = null,
    val overtime: Double = 0.0,
    val nightShift: Double = 0.0,
    val bonus: Double = 0.0,
    val costAllowance: Double = 0.0,
    val salaryDifference: Double = 0.0,
    val insalubrity: Double = 0.0,
    val hazardousDuty: Double = 0.0,
    val productivity: Double = 0.0,
    val totalCost: Double = 0.0,
    val advance: Double = 0.0,
    val payment: Double = 0.0,
    val netPayroll: Double = 0.0,
) {
    fun toDomain(): RhSummaryUnit =
        RhSummaryUnit(
            name = name,
            employees = employees,
            admitted = admitted,
            dismissed = dismissed,
            turnoverPercent = turnover,
            absenteeismPercent = absenteeism,
            overtime = overtime,
            nightShift = nightShift,
            bonus = bonus,
            costAllowance = costAllowance,
            salaryDifference = salaryDifference,
            insalubrity = insalubrity,
            hazardousDuty = hazardousDuty,
            productivity = productivity,
            totalCost = totalCost,
            advance = advance,
            payment = payment,
            netPayroll = netPayroll,
        )
}

@Serializable
private data class RhRankingDto(
    val name: String = "",
    val total: Double = 0.0,
    val employees: Int = 0,
) {
    fun toDomain(): RhRanking = RhRanking(name = name, total = total, employees = employees)
}

@Serializable
private data class RhDismissedDto(
    val total: Int = 0,
    val distinctJobs: Int = 0,
    val byReason: List<RhLabelCountDto> = emptyList(),
    val bySex: List<RhLabelCountDto> = emptyList(),
    val topJobTitles: List<RhLabelCountDto> = emptyList(),
    val topUnits: List<RhLabelCountDto> = emptyList(),
) {
    fun toDomain(): RhDismissed =
        RhDismissed(
            total = total,
            distinctJobs = distinctJobs,
            byReason = byReason.map(RhLabelCountDto::toDomain),
            bySex = bySex.map(RhLabelCountDto::toDomain),
            topJobTitles = topJobTitles.map(RhLabelCountDto::toDomain),
            topUnits = topUnits.map(RhLabelCountDto::toDomain),
        )
}

@Serializable
private data class RhLabelCountDto(
    val label: String = "",
    val count: Int = 0,
) {
    fun toDomain(): RhCountItem = RhCountItem(label = label, count = count)
}

@Serializable
private data class RhVacanciesDto(
    val total: Int = 0,
    val byStatus: List<RhNameQuantityDto> = emptyList(),
    val byUnit: List<RhNameQuantityDto> = emptyList(),
    val byFunction: List<RhNameQuantityDto> = emptyList(),
    val vacancies: List<RhVacancyDto> = emptyList(),
) {
    fun toDomain(): RhVacancies =
        RhVacancies(
            total = total,
            byStatus = byStatus.map(RhNameQuantityDto::toDomain),
            byUnit = byUnit.map(RhNameQuantityDto::toDomain),
            byFunction = byFunction.map(RhNameQuantityDto::toDomain),
            vacancies = vacancies.map(RhVacancyDto::toDomain),
        )
}

@Serializable
private data class RhNameQuantityDto(
    val name: String = "",
    val quantity: Int = 0,
) {
    fun toDomain(): RhCountItem = RhCountItem(label = name, count = quantity)
}

@Serializable
private data class RhVacancyDto(
    val rmpId: Long = 0,
    val status: String? = null,
    val statusRh: String? = null,
    val unitName: String? = null,
    val functionName: String? = null,
    val shift: String? = null,
    val contractType: String? = null,
    val requesterName: String? = null,
    val updatedAt: String? = null,
) {
    fun toDomain(): RhVacancy =
        RhVacancy(
            rmpId = rmpId,
            status = status,
            hrStatus = statusRh,
            unitName = unitName,
            functionName = functionName,
            shift = shift,
            contractType = contractType,
            requesterName = requesterName,
            updatedAt = updatedAt,
        )
}

@Serializable
private data class RhOvertimeDto(
    val monthly: List<RhOvertimeMonthDto> = emptyList(),
    val current: RhOvertimeMonthDto? = null,
    val byUnit: List<RhOvertimeGroupDto> = emptyList(),
    val byJobTitle: List<RhOvertimeGroupDto> = emptyList(),
    val byManager: List<RhOvertimeGroupDto> = emptyList(),
) {
    fun toDomain(): RhOvertime =
        RhOvertime(
            monthly = monthly.map(RhOvertimeMonthDto::toDomain),
            current = current?.toDomain(),
            byUnit = byUnit.map(RhOvertimeGroupDto::toDomain),
            byJobTitle = byJobTitle.map(RhOvertimeGroupDto::toDomain),
            byManager = byManager.map(RhOvertimeGroupDto::toDomain),
        )
}

@Serializable
private data class RhOvertimeMonthDto(
    val ano: Int,
    val mes: Int,
    val employees: Int = 0,
    val employeesWithOvertime: Int = 0,
    val overtime50: Double = 0.0,
    val overtime100: Double = 0.0,
    val overtimeDsr: Double = 0.0,
    val overtimeTotal: Double = 0.0,
    val averagePerEmployee: Double = 0.0,
    val overtimeShare: Double = 0.0,
    val productivity: Double = 0.0,
    val nightShift: Double = 0.0,
    val bonus: Double = 0.0,
    val costAllowance: Double = 0.0,
    val salaryDifference: Double = 0.0,
    val insalubrity: Double = 0.0,
    val hazardousDuty: Double = 0.0,
    val totalCost: Double = 0.0,
) {
    fun toDomain(): RhOvertimeMonth =
        RhOvertimeMonth(
            period = MonthPeriod(year = ano, month = mes),
            employees = employees,
            employeesWithOvertime = employeesWithOvertime,
            overtime50 = overtime50,
            overtime100 = overtime100,
            overtimeDsr = overtimeDsr,
            overtimeTotal = overtimeTotal,
            averagePerEmployee = averagePerEmployee,
            overtimeSharePercent = overtimeShare,
            productivity = productivity,
            nightShift = nightShift,
            bonus = bonus,
            costAllowance = costAllowance,
            salaryDifference = salaryDifference,
            insalubrity = insalubrity,
            hazardousDuty = hazardousDuty,
            totalCost = totalCost,
        )
}

@Serializable
private data class RhOvertimeGroupDto(
    val name: String = "",
    val employees: Int = 0,
    val employeesWithOvertime: Int = 0,
    val overtime50: Double = 0.0,
    val overtime100: Double = 0.0,
    val overtimeDsr: Double = 0.0,
    val overtimeTotal: Double = 0.0,
    val share: Double = 0.0,
) {
    fun toDomain(): RhOvertimeGroup =
        RhOvertimeGroup(
            name = name,
            employees = employees,
            employeesWithOvertime = employeesWithOvertime,
            overtime50 = overtime50,
            overtime100 = overtime100,
            overtimeDsr = overtimeDsr,
            overtimeTotal = overtimeTotal,
            sharePercent = share,
        )
}

@Serializable
private data class RhDashboardErrorDto(
    val message: String? = null,
)
